import LocalizedDateInput from 'components/Erp/LocalizedDateInput';
import { getLocale } from 'i18n';
import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import IntegrationsPanel from 'components/Erp/IntegrationsPanel'
import { LoadWeightCells, TripIntegrationDetails, EventActor } from 'components/Erp/IntegrationTrip'
import { getErpErrorMessage } from 'service/ErpService';
import React, { useState, useEffect } from 'react'
import ResponsiveTable from './ResponsiveTable'
import RowToggle from './LoadRowToggle/LoadRowToggle.tsx'
import AuditButton from './AuditButton'
import { DateTime, Vehicle, Duration } from 'components/Erp/Display'
import { MapPin, Image, Search, RefreshCw, Pencil, Truck, PackageOpen, History, ChartColumn, CircleAlert, LogOut, CalendarDays , ChevronLeft, ChevronRight, CheckCheck, X, Save } from 'lucide-react'
import { SectionHeading, EmptyState } from 'components/Erp/Presentation'
import s from './LoadsPanel.module.scss'
import { getLoadsInLoading, getLoadsInRoute, getLoadsCompleted, markExit, updateLoadNotes, completeTrip, getLoadsTimeline } from 'service/LoadService'
import moment from 'moment'
import Notification from 'utils/Notifications'
import { formatDuration } from 'pages/ReportCard/utils/formatDuration'
import { shouldToggleLoadRow } from 'pages/ReportCard/utils/rowInteraction'

// ─── Small helper components ──────────────────────────────────────────────────

const GeoBtn = ({ geo, label }) => {
  useInterfaceLanguage();
  if (!geo) return <span className={s.noneVal}>—</span>
  return (
    <button
      className={s.geoBtn}
      onClick={() => window.open(`https://www.google.com/maps?q=${geo}`, '_blank')}
    >
      <MapPin size={13}/> {tr(label || tr("Карта"))}
    </button>
  )
}

const PhotoBtn = ({ url, label }) => {
  useInterfaceLanguage();
  if (!url) return <span className={s.noneVal}>—</span>
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={s.photoLink}>
      <Image size={13}/> {tr(label || tr("Фото"))}
    </a>
  )
}

const DetailCard = ({ title, value, sub, wide }) => { useInterfaceLanguage(); return (<div className={`${s.detailCard}${wide ? ` ${s.wide}` : ''}`}>
    <div className={s.detailCardLabel}>{tr(title)}</div>
    <div className={s.detailCardValue}>
      {tr(value ?? <span className={s.noneVal}>—</span>)}
    </div>
    {tr(sub && <div className={s.detailCardSub}>{tr(sub)}</div>)}
  </div>); }

const SearchBar = ({ value, onChange, onSearch, onRefresh }) => { useInterfaceLanguage(); return (<div className={s.searchBar}>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSearch() }}
      placeholder={tr("Поиск по водителю, базе, номеру машины...")}
      className={s.searchInput}
    />
    <button className={s.searchIconBtn} aria-label={tr("Найти погрузки")} onClick={onSearch}><Search size={16}/></button>
    <button className={s.refreshIconBtn} aria-label={tr("Обновить погрузки")} onClick={onRefresh}><RefreshCw size={16}/></button>
  </div>); }

const PAGE_SIZE = 25
const localDate = date => {
  const parts = new Intl.DateTimeFormat('en', { timeZone:'Asia/Bishkek', year:'numeric', month:'2-digit', day:'2-digit' }).formatToParts(date)
  const part = type => parts.find(item => item.type === type).value
  return `${part('year')}-${part('month')}-${part('day')}`
}

const LoadsPanel = () => {
  useInterfaceLanguage();
  const [subTab, setSubTab] = useState('in_loading') // 'in_loading' | 'in_route' | 'history'

  // В погрузке
  const [loadingLoads, setLoadingLoads] = useState([])
  const [loadingLoadsPage, setLoadingLoadsPage] = useState(1)
  const [loadingLoadsCount, setLoadingLoadsCount] = useState(0)
  const [loadingLoadsSearch, setLoadingLoadsSearch] = useState('')

  // В пути
  const [activeLoads, setActiveLoads] = useState([])
  const [activeLoadsPage, setActiveLoadsPage] = useState(1)
  const [activeLoadsCount, setActiveLoadsCount] = useState(0)
  const [activeLoadsSearch, setActiveLoadsSearch] = useState('')

  // История
  const [historyLoads, setHistoryLoads] = useState([])
  const [historyLoadsPage, setHistoryLoadsPage] = useState(1)
  const [historyLoadsCount, setHistoryLoadsCount] = useState(0)
  const [historyLoadsSearch, setHistoryLoadsSearch] = useState('')

  // Раскрытые строки
  const [expandedRows, setExpandedRows] = useState({})
  const [loadError,setLoadError] = useState('')
  const [loadsFetching,setLoadsFetching] = useState(false)
  const [timelineError,setTimelineError] = useState('')

  // Геолокация
  const [userGeoLocation, setUserGeoLocation] = useState(null)

  // ─── Timeline ──────────────────────────────────────────────────────────────
  const todayStr = localDate(new Date())
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return localDate(d) }
  const [timeline, setTimeline] = useState(null)
  const [tlDateFrom, setTlDateFrom] = useState(daysAgo(14))
  const [tlDateTo, setTlDateTo] = useState(todayStr)
  const [tlPreset, setTlPreset] = useState('15d')
  const [tlLoading, setTlLoading] = useState(false)

  const TL_PRESETS = [
    { key: '15d',  label: "15 дней",     days: 14 },
    { key: '30d',  label: "За месяц",   days: 29 },
    { key: '90d',  label: "3 месяца",   days: 89 },
    { key: '180d', label: "6 месяцев",  days: 179 },
    { key: '365d', label: "Год",        days: 364 },
  ]

  const applyPreset = (preset) => {
    const from = daysAgo(preset.days)
    setTlDateFrom(from)
    setTlDateTo(todayStr)
    setTlPreset(preset.key)
    loadTimeline(from, todayStr)
  }

  // Модалки
  const [notesModal, setNotesModal] = useState({ isOpen: false, loadId: null })
  const [notesInput, setNotesInput] = useState('')
  const [notesSaving, setNotesSaving] = useState(false)

  const [completeTripModal, setCompleteTripModal] = useState({ isOpen: false, loadId: null })
  const [completeTripNotes, setCompleteTripNotes] = useState('')
  const [completeTripSaving, setCompleteTripSaving] = useState(false)

  const [markExitModal, setMarkExitModal] = useState({ isOpen: false, loadId: null })
  const [markExitSaving, setMarkExitSaving] = useState(false)

  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  // ─── Loaders ─────────────────────────────────────────────────────────────────

  const loadTimeline = async (from, to) => {
    setTlLoading(true)
    setTimelineError('')
    try {
      const res = await getLoadsTimeline({ date_from: from, date_to: to })
      setTimeline(res.data)
    } catch (err) { setTimelineError(getErpErrorMessage(err, tr("Не удалось загрузить график погрузок."))) }
    finally { setTlLoading(false) }
  }

  const loadInLoadingLoads = async (page = 1, search = '') => {
    setLoadsFetching(true);setLoadError('')
    try {
      const res = await getLoadsInLoading({ page, page_size: PAGE_SIZE, ...(search ? { search } : {}) })
      setLoadingLoads(res.data.results || [])
      setLoadingLoadsCount(res.data.count || 0)
      setLoadingLoadsPage(page)
    } catch (err) { setLoadError(getErpErrorMessage(err, tr("Не удалось загрузить погрузки."))) }
    finally { setLoadsFetching(false) }
  }

  const loadActiveLoads = async (page = 1, search = '') => {
    setLoadsFetching(true);setLoadError('')
    try {
      const res = await getLoadsInRoute({ page, page_size: PAGE_SIZE, ...(search ? { search } : {}) })
      setActiveLoads(res.data.results || [])
      setActiveLoadsCount(res.data.count || 0)
      setActiveLoadsPage(page)
    } catch (err) { setLoadError(getErpErrorMessage(err, tr("Не удалось загрузить рейсы в пути."))) }
    finally { setLoadsFetching(false) }
  }

  const loadHistoryLoads = async (page = 1, search = '') => {
    setLoadsFetching(true);setLoadError('')
    try {
      const res = await getLoadsCompleted({ page, page_size: PAGE_SIZE, ...(search ? { search } : {}) })
      setHistoryLoads(res.data.results || [])
      setHistoryLoadsCount(res.data.count || 0)
      setHistoryLoadsPage(page)
    } catch (err) { setLoadError(getErpErrorMessage(err, tr("Не удалось загрузить историю погрузок."))) }
    finally { setLoadsFetching(false) }
  }

  const reloadCurrent = async () => {
    if (subTab === 'in_loading') await loadInLoadingLoads(loadingLoadsPage, loadingLoadsSearch)
    else if (subTab === 'in_route') await loadActiveLoads(activeLoadsPage, activeLoadsSearch)
    else await loadHistoryLoads(historyLoadsPage, historyLoadsSearch)
  }

  useEffect(() => {
    loadTimeline(tlDateFrom, tlDateTo)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (subTab === 'in_loading') loadInLoadingLoads(1, loadingLoadsSearch)
    else if (subTab === 'in_route') loadActiveLoads(1, activeLoadsSearch)
    else loadHistoryLoads(1, historyLoadsSearch)
    setExpandedRows({})
  }, [subTab])

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserGeoLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => console.warn('Geolocation:', err)
      )
    }
  }, [])

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }))

  const elapsedSince = (timeStr) => {
    if (!timeStr) return null
    return formatDuration(timeStr, moment())
  }

  const geoStr = () => userGeoLocation ? `${userGeoLocation.lat},${userGeoLocation.lng}` : ''

  // Parse admin note prefix added by backend
  const parseCompletedBy = (item) => {
    const adminMatch = item.notes ? item.notes.match(/^\[Завершил: ([^\]]+)\]/) : null
    return item.operator_name || (adminMatch ? tr("{{value0}} (Адм.)", { value0: adminMatch[1] }) : null)
  }

  const parseNotesClean = (notes) => notes ? notes.replace(/^\[Завершил: [^\]]+\]\s*/, '') : ''

  // ─── Modal actions ────────────────────────────────────────────────────────────

  const openNotesModal = (loadId, currentNotes) => {
    setNotesModal({ isOpen: true, loadId })
    setNotesInput(currentNotes || '')
  }

  const saveNotes = async () => {
    setNotesSaving(true)
    try {
      await updateLoadNotes(notesModal.loadId, notesInput)
      await reloadCurrent()
      setNotesModal({ isOpen: false, loadId: null })
      setNotesInput('')
      setNotify({ isOpen: true, message: tr("Примечание сохранено"), type: 'success' })
    } catch (err) { setNotify({ isOpen: true, message: getErpErrorMessage(err,tr("Не удалось сохранить заметку.")), type: 'error' }) }
    finally { setNotesSaving(false) }
  }

  const handleCompleteTrip = async () => {
    if (!completeTripNotes.trim()) {
      setNotify({ isOpen: true, message: tr("Примечание обязательно"), type: 'error' })
      return
    }
    setCompleteTripSaving(true)
    try {
      await completeTrip(completeTripModal.loadId, completeTripNotes, geoStr())
      await reloadCurrent()
      setCompleteTripModal({ isOpen: false, loadId: null })
      setCompleteTripNotes('')
      setNotify({ isOpen: true, message: tr("Поездка завершена"), type: 'success' })
    } catch (err) { setNotify({ isOpen: true, message: getErpErrorMessage(err, tr("Ошибка завершения")), type: 'error' }) }
    finally { setCompleteTripSaving(false) }
  }

  const handleMarkExit = async () => {
    setMarkExitSaving(true)
    try {
      await markExit(markExitModal.loadId, { geo_a_exit: geoStr() })
      await loadInLoadingLoads(loadingLoadsPage, loadingLoadsSearch)
      setMarkExitModal({ isOpen: false, loadId: null })
      setNotify({ isOpen: true, message: tr("Выезд отмечен"), type: 'success' })
    } catch (err) { setNotify({ isOpen: true, message: getErpErrorMessage(err, tr("Ошибка выезда")), type: 'error' }) }
    finally { setMarkExitSaving(false) }
  }

  // ─── UI Pieces ────────────────────────────────────────────────────────────────

  const Pagination = ({ page, count, onPage }) => {
  useInterfaceLanguage();
    const pages = Math.ceil(count / PAGE_SIZE)
    if (pages <= 1) return null
    return (
      <div className={s.pagination}>
        <button className={s.paginationBtn} disabled={page === 1} onClick={() => onPage(page - 1)}><ChevronLeft size={14} aria-hidden="true"/>{tr(" Пред.")}</button>
        <span className={s.paginationInfo}>{tr("Стр. ")}{tr(page)} / {tr(pages)} · {tr(count)}{tr(" записей")}</span>
        <button className={s.paginationBtn} disabled={page >= pages} onClick={() => onPage(page + 1)}><ChevronRight size={14} aria-hidden="true"/>{tr(" След.")}</button>
      </div>
    )
  }

  const ActionBtns = ({ row, tab }) => { useInterfaceLanguage(); return (<div className={s.actionBtns}>
      {tr(tab === 'in_loading' && (
        <button className={s.btnExit} onClick={() => setMarkExitModal({ isOpen: true, loadId: row.id })}>
          <LogOut size={14} aria-hidden="true"/>{tr(" Выезд")}</button>
      ))}
      {tr(tab !== 'history' && (
        <button className={s.btnComplete} onClick={() => { setCompleteTripModal({ isOpen: true, loadId: row.id }); setCompleteTripNotes('') }}><CheckCheck size={14} aria-hidden="true"/>{tr("Завершить")}</button>
      ))}
      <button className={s.btnNotes} onClick={() => openNotesModal(row.id, row.notes)}><Pencil size={13}/>{tr(" Заметка")}</button>
      <AuditButton section="loads" objectId={row.id} objectType="tabel.Load"/>
    </div>); }

  // ─── Tab: В погрузке ─────────────────────────────────────────────────────────

  const renderLoadingLoads = () => (
    <>
      <SearchBar
        value={loadingLoadsSearch} onChange={setLoadingLoadsSearch}
        onSearch={() => loadInLoadingLoads(1, loadingLoadsSearch)}
        onRefresh={() => loadInLoadingLoads(loadingLoadsPage, loadingLoadsSearch)}
      />
      <div className={s.tableWrapper}>
        <ResponsiveTable className={s.loadsTable}>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>{tr("Авто / Модель")}</th>
              <th>{tr("Водитель")}</th>
              <th>{tr("Телефон")}</th>
              <th>{tr("Очередь")}</th><th>{tr("Масса тары")}</th><th>{tr("Брутто · нетто")}</th>
              <th>{tr("База")}</th>
              <th>{tr("Въезд (А)")}</th>
              <th>{tr("Охранник въезда")}</th>
              <th>{tr("В погрузке")}</th>
              <th>{tr("Действия")}</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(loadingLoads) ? loadingLoads : []).map((item, index) => {
              const seq = (loadingLoadsPage - 1) * PAGE_SIZE + index + 1
              const elapsed = elapsedSince(item.time_a)
              const isExpanded = !!expandedRows[item.id]
              return (
                <React.Fragment key={item.id}>
                  <tr
                    className={`${s.dataRow}${isExpanded ? ` ${s.rowExpanded}` : ''}`}
                    onClick={event => { if (shouldToggleLoadRow(event)) toggleRow(item.id) }}
                  >
                    <td><RowToggle className={s.paginationBtn} item={item} expanded={isExpanded} sequence={seq} controls={`admin-load-details-loading-${item.id}`} onToggle={toggleRow} /></td>
                    <td>
                      <Vehicle number={item.vehicle_number} model={item.vehicle_model}/>
                    </td>
                    <td><span className={s.driverName}>{tr(item.employee_name || '—')}</span></td>
                    <td><span className={s.phoneText}>{tr(item.employee_phone || '—')}</span></td>
                    <LoadWeightCells load={item} /><td><span className={s.baseName}>{tr(item.top_base_name || item.chpp_name || item.private_point_name || '—')}</span></td>
                    <td><DateTime value={item.time_a}/></td>
                    <td><span className={s.phoneText}><EventActor source={item.entered_source} label={item.entered_actor_label} fallback={item.guard_name} entry /></span></td>
                    <td><Duration value={elapsed} label={tr("В погрузке")} /></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionBtns row={item} tab="in_loading" />
                    </td>
                  </tr>
                  {tr(isExpanded && (
                    <tr id={`admin-load-details-loading-${item.id}`} className={s.detailRow}>
                      <td colSpan={12}>
                        <div className={s.expandDetail}><TripIntegrationDetails load={item} />
                          <div className={s.detailGrid}>
                            <DetailCard title={tr("ID записи")} value={`#${item.id}`} />
                            <DetailCard title={tr("Автомобиль")} value={<Vehicle number={item.vehicle_number} model={item.vehicle_model} />} />
                            <DetailCard title={tr("В погрузке")} value={<Duration value={elapsed} />} />
                            <DetailCard title={tr("Охранник въезда")} value={item.guard_name} sub={item.guard_phone} />
                            <DetailCard title={tr("Гео въезда (А)")} value={<GeoBtn geo={item.geo_a} label={tr("Открыть")} />} />
                            <DetailCard title={tr("Фото въезда (А)")} value={<PhotoBtn url={item.photo_a} label={tr("Смотреть")} />} />
                            <DetailCard title={tr("Статус водителя")} value={item.driver_status} />
                            <DetailCard title={tr("Создана")} value={<DateTime value={item.created_at}/>} />
                            {tr(item.notes && <DetailCard title={tr("Примечания")} value={parseNotesClean(item.notes)} wide />)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              )
            })}
            {tr(loadingLoads.length === 0 && (
              <tr className={s.emptyRow}>
                <td colSpan={12}><EmptyState icon={PackageOpen} title={tr("Нет погрузок со статусом «В погрузке»")} description={tr("Активные погрузки появятся после въезда водителя в Кара-Кече.")} compact /></td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>
      <Pagination page={loadingLoadsPage} count={loadingLoadsCount} onPage={(p) => loadInLoadingLoads(p, loadingLoadsSearch)} />
    </>
  )

  // ─── Tab: В пути ─────────────────────────────────────────────────────────────

  const renderActiveLoads = () => (
    <>
      <SearchBar
        value={activeLoadsSearch} onChange={setActiveLoadsSearch}
        onSearch={() => loadActiveLoads(1, activeLoadsSearch)}
        onRefresh={() => loadActiveLoads(activeLoadsPage, activeLoadsSearch)}
      />
      <div className={s.tableWrapper}>
        <ResponsiveTable className={s.loadsTable}>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>{tr("Авто / Модель")}</th>
              <th>{tr("Водитель")}</th>
              <th>{tr("Телефон")}</th>
              <th>{tr("Очередь")}</th><th>{tr("Масса тары")}</th><th>{tr("Брутто · нетто")}</th>
              <th>{tr("База")}</th>
              <th>{tr("Въезд (А)")}</th>
              <th>{tr("Выезд (А)")}</th>
              <th>{tr("Охранник выезда")}</th>
              <th>{tr("В пути")}</th>
              <th>{tr("Действия")}</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(activeLoads) ? activeLoads : []).map((item, index) => {
              const seq = (activeLoadsPage - 1) * PAGE_SIZE + index + 1
              const elapsed = elapsedSince(item.time_a_exit)
              const isExpanded = !!expandedRows[item.id]
              return (
                <React.Fragment key={item.id}>
                  <tr
                    className={`${s.dataRow}${isExpanded ? ` ${s.rowExpanded}` : ''}`}
                    onClick={event => { if (shouldToggleLoadRow(event)) toggleRow(item.id) }}
                  >
                    <td><RowToggle className={s.paginationBtn} item={item} expanded={isExpanded} sequence={seq} controls={`admin-load-details-route-${item.id}`} onToggle={toggleRow} /></td>
                    <td>
                      <Vehicle number={item.vehicle_number} model={item.vehicle_model}/>
                    </td>
                    <td><span className={s.driverName}>{tr(item.employee_name || '—')}</span></td>
                    <td><span className={s.phoneText}>{tr(item.employee_phone || '—')}</span></td>
                    <LoadWeightCells load={item} /><td><span className={s.baseName}>{tr(item.top_base_name || item.chpp_name || item.private_point_name || '—')}</span></td>
                    <td><DateTime value={item.time_a}/></td>
                    <td><DateTime value={item.time_a_exit}/></td>
                    <td><span className={s.phoneText}><EventActor source={item.exit_source} label={item.exit_actor_label} fallback={item.exit_guard_name} /></span></td>
                    <td><Duration value={elapsed} label={tr("В пути")} /></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionBtns row={item} tab="in_route" />
                    </td>
                  </tr>
                  {tr(isExpanded && (
                    <tr id={`admin-load-details-route-${item.id}`} className={s.detailRow}>
                      <td colSpan={13}>
                        <div className={s.expandDetail}><TripIntegrationDetails load={item} />
                          <div className={s.detailGrid}>
                            <DetailCard title={tr("ID записи")} value={`#${item.id}`} />
                            <DetailCard title={tr("Автомобиль")} value={<Vehicle number={item.vehicle_number} model={item.vehicle_model} />} />
                            <DetailCard title={tr("В пути")} value={<Duration value={elapsed} />} />
                            <DetailCard title={tr("Охранник въезда (А)")} value={item.guard_name} sub={item.guard_phone} />
                            <DetailCard title={tr("Охранник выезда (А)")} value={item.exit_guard_name} sub={item.exit_guard_phone} />
                            <DetailCard title={tr("Время погрузки")} value={<Duration value={item.duration_str_loading} />} />
                            <DetailCard title={tr("Гео въезда (А)")} value={<GeoBtn geo={item.geo_a} label={tr("Открыть")} />} />
                            <DetailCard title={tr("Гео выезда (А)")} value={<GeoBtn geo={item.geo_a_exit} label={tr("Открыть")} />} />
                            <DetailCard title={tr("Фото въезда (А)")} value={<PhotoBtn url={item.photo_a} label={tr("Смотреть")} />} />
                            <DetailCard title={tr("Статус водителя")} value={item.driver_status} />
                            {tr(item.notes && <DetailCard title={tr("Примечания")} value={parseNotesClean(item.notes)} wide />)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              )
            })}
            {tr(activeLoads.length === 0 && (
              <tr className={s.emptyRow}>
                <td colSpan={13}><EmptyState icon={Truck} title={tr("Нет погрузок со статусом «В пути»")} description={tr("Здесь отображаются автомобили после отметки выезда из Кара-Кече.")} compact /></td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>
      <Pagination page={activeLoadsPage} count={activeLoadsCount} onPage={(p) => loadActiveLoads(p, activeLoadsSearch)} />
    </>
  )

  // ─── Tab: История ─────────────────────────────────────────────────────────────

  const renderHistoryLoads = () => (
    <>
      <SearchBar
        value={historyLoadsSearch} onChange={setHistoryLoadsSearch}
        onSearch={() => loadHistoryLoads(1, historyLoadsSearch)}
        onRefresh={() => loadHistoryLoads(historyLoadsPage, historyLoadsSearch)}
      />
      <div className={s.tableWrapper}>
        <ResponsiveTable className={s.loadsTable}>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>{tr("Авто")}</th>
              <th>{tr("Водитель")}</th>
              <th>{tr("Телефон")}</th>
              <th>{tr("Очередь")}</th><th>{tr("Масса тары")}</th><th>{tr("Брутто · нетто")}</th>
              <th>{tr("База")}</th>
              <th>{tr("Въезд (А)")}</th>
              <th>{tr("Выезд (А)")}</th>
              <th>{tr("Прибытие (Б)")}</th>
              <th>{tr("Погрузка")}</th>
              <th>{tr("Доставка")}</th>
              <th>{tr("Итого")}</th>
              <th>{tr("Кто завершил")}</th>
              <th>{tr("Действия")}</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(historyLoads) ? historyLoads : []).map((item, index) => {
              const seq = (historyLoadsPage - 1) * PAGE_SIZE + index + 1
              const isExpanded = !!expandedRows[item.id]
              const completedBy = parseCompletedBy(item)
              const notesClean = parseNotesClean(item.notes)
              return (
                <React.Fragment key={item.id}>
                  <tr
                    className={`${s.dataRow}${isExpanded ? ` ${s.rowExpanded}` : ''}`}
                    onClick={event => { if (shouldToggleLoadRow(event)) toggleRow(item.id) }}
                  >
                    <td><RowToggle className={s.paginationBtn} item={item} expanded={isExpanded} sequence={seq} controls={`admin-load-details-history-${item.id}`} onToggle={toggleRow} /></td>
                    <td>
                      <Vehicle number={item.vehicle_number} model={item.vehicle_model}/>
                    </td>
                    <td><span className={s.driverName}>{tr(item.employee_name || '—')}</span></td>
                    <td><span className={s.phoneText}>{tr(item.employee_phone || '—')}</span></td>
                    <LoadWeightCells load={item} /><td><span className={s.baseName}>{tr(item.top_base_name || item.chpp_name || item.private_point_name || '—')}</span></td>
                    <td><DateTime value={item.time_a}/></td>
                    <td><DateTime value={item.time_a_exit}/></td>
                    <td><DateTime value={item.time_b}/></td>
                    <td><Duration value={item.duration_str_loading} label={tr("Погрузка")} /></td>
                    <td><Duration value={item.duration_str} label={tr("Доставка")} /></td>
                    <td><Duration value={item.total_duration_str} label={tr("Общее")} /></td>
                    <td><span className={completedBy ? s.completedBy : s.noneVal}>{tr(completedBy || '—')}</span></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionBtns row={item} tab="history" />
                    </td>
                  </tr>
                  {tr(isExpanded && (
                    <tr id={`admin-load-details-history-${item.id}`} className={s.detailRow}>
                      <td colSpan={16}>
                        <div className={s.expandDetail}><TripIntegrationDetails load={item} />
                          <div className={s.detailGrid}>
                            <DetailCard title={tr("ID записи")} value={`#${item.id}`} />
                            <DetailCard title={tr("Автомобиль")} value={<Vehicle number={item.vehicle_number} model={item.vehicle_model} />} />
                            <DetailCard title={tr("Охранник въезда (А)")} value={item.guard_name} sub={item.guard_phone} />
                            <DetailCard title={tr("Охранник выезда (А)")} value={item.exit_guard_name} sub={item.exit_guard_phone} />
                            <DetailCard title={tr("Кто завершил (Б)")} value={completedBy} sub={item.by_guard_phone} />
                            <DetailCard title={tr("Время погрузки")} value={<Duration value={item.duration_str_loading} />} />
                            <DetailCard title={tr("Время доставки")} value={<Duration value={item.duration_str} />} />
                            <DetailCard title={tr("Итого")} value={<Duration value={item.total_duration_str} />} />
                            <DetailCard title={tr("Гео въезда (А)")} value={<GeoBtn geo={item.geo_a} label={tr("Открыть")} />} />
                            <DetailCard title={tr("Гео выезда (А)")} value={<GeoBtn geo={item.geo_a_exit} label={tr("Открыть")} />} />
                            <DetailCard title={tr("Гео прибытия (Б)")} value={<GeoBtn geo={item.geo_b} label={tr("Открыть")} />} />
                            <DetailCard title={tr("Фото въезда (А)")} value={<PhotoBtn url={item.photo_a} label={tr("Смотреть")} />} />
                            <DetailCard title={tr("Фото прибытия (Б)")} value={<PhotoBtn url={item.photo_b} label={tr("Смотреть")} />} />
                            <DetailCard title={tr("Статус водителя")} value={item.driver_status} />
                            {tr(notesClean && <DetailCard title={tr("Примечания")} value={notesClean} wide />)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              )
            })}
            {tr(historyLoads.length === 0 && (
              <tr className={s.emptyRow}>
                <td colSpan={16}><EmptyState icon={History} title={tr("История пуста")} description={tr("Завершённые поездки и данные прибытия сохраняются здесь.")} compact /></td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>
      <Pagination page={historyLoadsPage} count={historyLoadsCount} onPage={(p) => loadHistoryLoads(p, historyLoadsSearch)} />
    </>
  )

  // ─── Timeline chart ──────────────────────────────────────────────────────────

  const renderTimeline = () => {
    const data = (timeline && timeline.timeline) ? timeline.timeline : []
    const maxVal = Math.max(...data.map(d => d.total), 1)

    const W = 1000
    const H = 200
    const padL = 44, padR = 16, padT = 20, padB = 40
    const chartW = W - padL - padR
    const chartH = H - padT - padB
    const n = data.length
    const slotW = n > 0 ? chartW / n : chartW
    const barW = Math.max(4, slotW - 4)

    return (
      <div className={s.timelineSection}>
        {/* Stats + date picker row */}
        <div className={s.timelineTop}>
          <div className={s.timelineStats}>
            <div className={s.timelineStat}>
              <span className={s.timelineStatLabel}><CalendarDays size={13} aria-hidden="true"/>{tr(" С первого сканирования")}</span>
              <span className={s.timelineStatDate}>{timeline && timeline.first_scan_date ? moment(timeline.first_scan_date).format('DD.MM.YYYY') : '—'}</span>
            </div>
            <div className={s.timelineStatDivider} />
            <div className={s.timelineStat}>
              <span className={s.timelineStatLabel}><ChartColumn size={13} aria-hidden="true"/>{tr(" Всего создано записей")}</span>
              <span className={s.timelineStatBig}>{timeline ? timeline.total_scanned.toLocaleString(getLocale()) : '—'}</span>
            </div>
          </div>
          <div className={s.tlControls}>
            <div className={s.tlPresets}>
              {TL_PRESETS.map(p => (
                <button
                  key={p.key}
                  className={`${s.tlPresetBtn}${tlPreset === p.key ? ` ${s.tlPresetBtnActive}` : ''}`}
                  onClick={() => applyPreset(p)}
                  disabled={tlLoading}
                >{tr(p.label)}</button>
              ))}
            </div>
            <div className={s.timelineDateRange}>
              <label className={s.tlLabel}>{tr("с")}</label>
              <LocalizedDateInput aria-label={tr("с")}
                
                value={tlDateFrom}
                max={tlDateTo}
                onChange={e => { setTlDateFrom(e.target.value); setTlPreset(null) }}
                className={s.tlDateInput}
              />
              <label className={s.tlLabel}>{tr("по")}</label>
              <LocalizedDateInput aria-label={tr("по")}
                
                value={tlDateTo}
                min={tlDateFrom}
                max={todayStr}
                onChange={e => { setTlDateTo(e.target.value); setTlPreset(null) }}
                className={s.tlDateInput}
              />
              <button
                className={s.tlRefreshBtn}
                aria-label={tr("Обновить график погрузок")}
                onClick={() => loadTimeline(tlDateFrom, tlDateTo)}
                disabled={tlLoading}
              >
                <RefreshCw size={15} aria-hidden="true"/>
              </button>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className={s.timelineChartWrap}>
          {tr(data.length === 0 && !tlLoading && (
            <EmptyState icon={ChartColumn} title={tr("Нет данных за выбранный период")} description={tr("Выберите другой период, чтобы увидеть динамику погрузок.")} compact />
          ))}
          {tr(tlLoading && <div className={s.tlLoadingOverlay}>{tr("Загрузка...")}</div>)}
          {tr(data.length > 0 && (
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className={s.tlSvg}
              preserveAspectRatio="none"
            >
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
                const y = padT + chartH * (1 - ratio)
                const val = Math.round(maxVal * ratio)
                return (
                  <g key={ratio}>
                    <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--theme-border, #eaecf2)" strokeWidth="1" />
                    <text x={padL - 6} y={y + 4} textAnchor="end" fontSize="11" fill="var(--theme-text-subtle, #939db5)" fontFamily="inherit">{tr(val)}</text>
                  </g>
                )
              })}

              {/* Bars */}
              {data.map((d, i) => {
                const x = padL + i * slotW + (slotW - barW) / 2
                const barH = d.total > 0 ? Math.max(2, (d.total / maxVal) * chartH) : 2
                const y = padT + chartH - barH
                const label = moment(d.date).format('DD.MM')
                const isWeekend = [0, 6].includes(new Date(d.date + 'T12:00:00').getDay())
                const showLabel = n <= 31 || i % Math.ceil(n / 25) === 0
                const barFill = d.total === 0 ? '#dde2ee' : isWeekend ? '#7c8db0' : '#0b1728'

                return (
                  <g key={d.date}>
                    <rect x={x} y={y} width={barW} height={barH} rx="2" fill={barFill}>
                      <title>{tr("{{value0}}: {{value1}} погр.", { value0: moment(d.date).format('DD.MM.YYYY'), value1: d.total })}</title>
                    </rect>
                    {tr(d.total > 0 && barH > 18 && (
                      <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize="9" fill="var(--theme-text-muted, #555)" fontFamily="inherit">
                        {tr(d.total)}
                      </text>
                    ))}
                    {tr(showLabel && (
                      <text
                        x={x + barW / 2}
                        y={H - padB + 14}
                        textAnchor="end"
                        fontSize="10"
                        fill={isWeekend ? "var(--theme-info-text, #7c8db0)" : "var(--theme-text-subtle, #939db5)"}
                        fontFamily="inherit"
                        transform={`rotate(-40, ${x + barW / 2}, ${H - padB + 14})`}
                      >
                        {tr(label)}
                      </text>
                    ))}
                  </g>
                )
              })}

              {/* X axis line */}
              <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="var(--theme-info-border, #dde2ee)" strokeWidth="1" />
            </svg>
          ))}
        </div>
      </div>
    )
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className={s.loadsPanel}>
      {/* Header */}
      <div className={s.loadsPanelHeader}><SectionHeading icon={Truck} title={tr("Погрузки")} description={tr("Погрузка, движение и завершение рейсов с документированной историей.")} /></div>

      {/* Timeline + stats */}
      {tr(timelineError && <div role="alert" style={{padding:'12px 18px',color:"var(--theme-danger-text, #b73030)"}}>{tr(timelineError)}<button className={s.paginationBtn} onClick={()=>loadTimeline(tlDateFrom,tlDateTo)}><RefreshCw size={14} aria-hidden="true"/>{tr(" Повторить")}</button></div>)}
      {renderTimeline()}

      {/* Tab bar */}
      <div className={s.tabBar}>
        {[['entered', tr("Въезд")], ['queued', tr("В очереди")], ['repair', tr("На ремонте")], ['lanes', tr("Полосы")]].map(([key,label]) => <button key={key} className={s.tabBtn + (subTab === key ? ' ' + s.tabBtnActive : '')} onClick={() => setSubTab(key)}><Truck size={16} />{tr(label)}</button>)}
        <button
          className={`${s.tabBtn}${subTab === 'in_loading' ? ` ${s.tabBtnActive}` : ''}`}
          onClick={() => setSubTab('in_loading')}
        >
          <PackageOpen size={16} aria-hidden="true"/>{tr(" В погрузке")}</button>
        <button
          className={`${s.tabBtn}${subTab === 'in_route' ? ` ${s.tabBtnActive}` : ''}`}
          onClick={() => setSubTab('in_route')}
        >
          <Truck size={16} aria-hidden="true"/>{tr(" В пути")}</button>
        <button
          className={`${s.tabBtn}${subTab === 'history' ? ` ${s.tabBtnActive}` : ''}`}
          onClick={() => setSubTab('history')}
        >
          <History size={16} aria-hidden="true"/>{tr(" История")}</button>
      </div>

      {/* Body */}
      <div className={s.loadsPanelBody}>
        {tr(loadError && <div role="alert" style={{padding:12,color:"var(--theme-danger-text, #b73030)",background:"var(--theme-surface-muted, #fff5f4)",borderRadius:7,marginBottom:12}}>{tr(loadError)}<button className={s.paginationBtn} onClick={reloadCurrent}><RefreshCw size={14} aria-hidden="true"/>{tr(" Повторить")}</button></div>)}
        {tr(loadsFetching && <p role="status" style={{fontSize:13,color:"var(--theme-text-muted, #77828e)"}}>{tr("Загрузка погрузок…")}</p>)}
        {tr(['entered','queued','repair'].includes(subTab) && <IntegrationsPanel key={subTab} resource="loads" stage={subTab} />)}
        {tr(subTab === 'lanes' && <IntegrationsPanel resource="queues" />)}
        {tr(subTab === 'in_loading' && renderLoadingLoads())}
        {tr(subTab === 'in_route' && renderActiveLoads())}
        {tr(subTab === 'history' && renderHistoryLoads())}
      </div>

      {/* Modal: Примечания */}
      {tr(notesModal.isOpen && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <div className={s.modalTitle}><Pencil size={18} aria-hidden="true"/>{tr(" Примечания")}</div>
            <textarea
              className={s.modalTextarea}
              value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              placeholder={tr("Введите примечания...")}
            />
            <div className={s.modalActions}>
              <button className={s.modalBtnCancel} onClick={() => setNotesModal({ isOpen: false, loadId: null })}><X size={14} aria-hidden="true"/>{tr("Отмена")}</button>
              <button className={s.modalBtnPrimary} onClick={saveNotes} disabled={notesSaving}><Save size={14} aria-hidden="true"/>
                {notesSaving ? tr("Сохранение...") : tr("Сохранить")}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Modal: Завершение поездки */}
      {tr(completeTripModal.isOpen && (
        <div className={s.modalOverlay}>
          <div className={`${s.modal} ${s.dangerModal}`}>
            <div className={s.modalTitleDanger}><CircleAlert size={18} aria-hidden="true"/>{tr(" Завершение поездки")}</div>
            <div className={s.modalBody}>{tr("Текущий администратор будет записан как завершивший данную поездку.")}</div>
            <textarea
              className={s.modalTextarea}
              value={completeTripNotes}
              onChange={(e) => setCompleteTripNotes(e.target.value)}
              placeholder={tr("Введите примечание (обязательно)...")}
            />
            {tr(userGeoLocation && (
              <div className={s.modalGeo}>
                <MapPin size={13}/> {userGeoLocation.lat.toFixed(6)}, {userGeoLocation.lng.toFixed(6)}
              </div>
            ))}
            <div className={s.modalActions}>
              <button className={s.modalBtnCancel} onClick={() => setCompleteTripModal({ isOpen: false, loadId: null })}><X size={14} aria-hidden="true"/>{tr("Отмена")}</button>
              <button
                className={s.modalBtnDanger}
                onClick={handleCompleteTrip}
                disabled={completeTripSaving || !completeTripNotes.trim()}
              ><CheckCheck size={14} aria-hidden="true"/>
                {completeTripSaving ? tr("Завершение...") : tr("Завершить")}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Modal: Отметка выезда */}
      {tr(markExitModal.isOpen && (
        <div className={s.modalOverlay}>
          <div className={`${s.modal} ${s.successModal}`}>
            <div className={s.modalTitleSuccess}><LogOut size={18} aria-hidden="true"/>{tr(" Подтверждение выезда")}</div>
            <div className={s.modalBody}>{tr("Вы действительно хотите отметить выезд для данной погрузки?")}</div>
            {tr(userGeoLocation && (
              <div className={s.modalGeo}>
                <MapPin size={13}/> {userGeoLocation.lat.toFixed(6)}, {userGeoLocation.lng.toFixed(6)}
              </div>
            ))}
            <div className={s.modalActions}>
              <button className={s.modalBtnCancel} onClick={() => setMarkExitModal({ isOpen: false, loadId: null })}><X size={14} aria-hidden="true"/>{tr("Отмена")}</button>
              <button className={s.modalBtnSuccess} onClick={handleMarkExit} disabled={markExitSaving}><LogOut size={14} aria-hidden="true"/>
                {markExitSaving ? tr("Обработка...") : tr("Подтвердить выезд")}
              </button>
            </div>
          </div>
        </div>
      ))}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default LoadsPanel
